import { insertMatchResults } from './db-utils'; // Import the Supabase insertion function

const CRICAPI_ENDPOINT = 'https://api.cricapi.com/v1/series_info?apikey=4fb83d4c-9b2a-4ef2-b43f-046817596204&id=d5a498c8-7596-4b93-8ab0-e0efc3345312';

/**
 * Fetches series information from the CricAPI endpoint.
 *
 * @returns {Promise<object|null>} A promise that resolves with the parsed JSON data,
 *                                or null if an error occurred.
 */
export const fetchSeriesInfo = async () => {
  console.log(`Fetching data from CricAPI: ${CRICAPI_ENDPOINT}`);
  try {
    const response = await fetch(CRICAPI_ENDPOINT);

    if (!response.ok) {
      // Handle HTTP errors (e.g., 404 Not Found, 500 Internal Server Error)
      console.error(`HTTP error fetching CricAPI data: ${response.status} ${response.statusText}`);
      // Attempt to read error details from the response body if available
      try {
        const errorBody = await response.json();
        console.error('CricAPI error details:', errorBody);
      } catch (parseError) {
        console.error('Could not parse error response body.');
      }
      return null;
    }

    const data = await response.json();
    // console.log('Successfully fetched data from CricAPI.');
    return data;

  } catch (error) {
    // Handle network errors or other issues with the fetch request
    console.error('Error during fetch operation for CricAPI:', error);
    return null;
  }
};

/**
 * Orchestrates fetching data from CricAPI and inserting it into Supabase.
 */
export const syncCricApiToSupabase = async () => {
  console.log('Starting CricAPI to Supabase sync process...');

  // 1. Fetch data from CricAPI
  const apiData = await fetchSeriesInfo();

  if (!apiData) {
    console.error('Sync failed: Could not fetch data from CricAPI.');
    return; // Stop the process if fetching failed
  }

  // 2. Insert data into Supabase (using the placeholder function)
  console.log('Proceeding to insert fetched data into Supabase...');
  const { error: insertError } = await insertMatchResults(apiData);

  if (insertError) {
    console.error('Sync failed: Error during Supabase insertion.', insertError);
  } else {
    console.log('Sync process completed (Note: Supabase insertion logic is currently a placeholder).');
  }
};

/**
 * Sorts an array of IPL match objects chronologically based on their dateTimeGMT.
 * This version is designed to be exported as an ES Module.
 *
 * @param {object} matchData The 'data' object from the API response,
 * containing the 'matchList' array.
 * @returns {Array} A new array containing the match objects sorted by date and time,
 * or an empty array if the input is invalid.
 */
export default function sortIPLMatchesChronologically(matchData) {
  // --- Input Validation ---
  // Check if input is provided and has the required 'matchList' array
  if (!matchData || !Array.isArray(matchData.matchList)) {
    console.error("Invalid input: Expected an object with a 'matchList' array.");
    return []; // Return an empty array to indicate failure or invalid input
  }

  // --- Sorting Logic ---
  // 1. Create a shallow copy of the original array to avoid modification.
  const matchesToSort = [...matchData.matchList];

  // 2. Use the Array.prototype.sort() method with a custom comparison function.
  matchesToSort.sort((matchA, matchB) => {
    // Convert the dateTimeGMT strings into JavaScript Date objects.
    const dateA = new Date(matchA.dateTimeGMT);
    const dateB = new Date(matchB.dateTimeGMT);

    // Subtracting Date objects gives the correct comparison result for ascending order.
    return dateA - dateB;
  });

  // 3. Return the newly sorted array.
  return matchesToSort;
}
